import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useMemo } from "react";
import { ALARM_STEPS_CONFIG, AlarmWizardStore, WizardFormData } from "./AlarmWizardStore";
import { AlarmWizardStoreProvider, useAlarmWizardStore } from "./useAlarmWizardStore";
import { observer } from "mobx-react-lite";
import { useStore } from "@/hooks/useStore";
import { WizardStepIndicator } from "../components/WizardStepIndicator";
import { AlarmWizardFotter } from "./components/AlarmWizardFooter";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { wizardSchema } from "../wizardSchema";
import { StepAlarmType } from "./StepAlarmType";
import { StepCondition } from "./StepCondition";
import { StepScope } from "./StepScope";
import { StepRecipients } from "./StepRecipients";
import { Spinner } from "@/components/Spinner";
import { AnimatePresence, motion } from "framer-motion";

const collectErrorMessages = (node: unknown): string[] => {
	if (!node || typeof node !== "object") return [];
	const messages: string[] = [];
	const message = (node as { message?: unknown }).message;
	if (typeof message === "string" && message) messages.push(message);
	for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
		if (key === "message" || key === "type" || key === "ref" || key === "types") continue;
		messages.push(...collectErrorMessages(value));
	}
	return messages;
};

const AlarmWizardBody = observer(() => {
	const state = useAlarmWizardStore();
	const { currentStep, isFetchingAlarm, fetchAlarmError, submitError } = state;
	const { errors } = useFormState({ control: state.form.control });
	const isLastStep = currentStep === ALARM_STEPS_CONFIG.length - 1;
	// On the last step, submitting validates the whole form — surface errors from
	// any earlier step too so the user can see why "Update / Create" is rejected
	// (the FormMessage on the offending field lives on a step that's unmounted).
	const errorRoots: unknown[] = isLastStep
		? Object.values(errors)
		: (ALARM_STEPS_CONFIG[currentStep]?.fields ?? []).map(fieldName => errors[fieldName]);
	const stepErrorMessages = Array.from(new Set(errorRoots.flatMap(collectErrorMessages)));

	// Backend 400s come back with a structured `validationErrors` map; flatten to a list.
	// Other status codes only have `message`.
	const submitMessages: string[] = (() => {
		if (!submitError) return [];
		const validation = submitError.validationErrors;
		if (submitError.status === 400 && validation && Object.keys(validation).length > 0) {
			return Object.values(validation).flat();
		}
		return [submitError.message || "Couldn't save alarm. Please try again."];
	})();

	if (isFetchingAlarm) {
		return (
			<div className="flex-1 overflow-y-auto px-6 py-6 flex items-center justify-center">
				<Spinner messages={["Getting latest alarm data..."]} loaderClassName="text-primary opacity-90" />
			</div>
		);
	}

	if (fetchAlarmError) {
		return (
			<div className="flex-1 overflow-y-auto px-6 py-6">
				<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
					{fetchAlarmError}
				</div>
			</div>
		);
	}

	return (
		<div className="flex-1 overflow-y-auto px-6 py-6">
			{currentStep === 0 && <StepAlarmType />}
			{currentStep === 1 && <StepCondition />}
			{currentStep === 2 && <StepScope />}
			{currentStep === 3 && <StepRecipients />}
			<AnimatePresence initial={false}>
				{stepErrorMessages.length > 0 && (
					<motion.div
						key="step-errors"
						initial={{ opacity: 0, height: 0, marginTop: 0 }}
						animate={{ opacity: 1, height: "auto", marginTop: 20 }}
						exit={{ opacity: 0, height: 0, marginTop: 0 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						className="overflow-hidden"
					>
						<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive space-y-0.5">
							{stepErrorMessages.map((message, index) => (
								<div key={index}>{message}</div>
							))}
						</div>
					</motion.div>
				)}
				{submitMessages.length > 0 && (
					<motion.div
						key="submit-error"
						initial={{ opacity: 0, height: 0, marginTop: 0 }}
						animate={{ opacity: 1, height: "auto", marginTop: 20 }}
						exit={{ opacity: 0, height: 0, marginTop: 0 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						className="overflow-hidden"
					>
						<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
							<p className="font-medium">Couldn't save alarm</p>
							<ul className="mt-1 list-inside list-disc space-y-0.5 text-xs">
								{submitMessages.map((message, index) => (
									<li key={index}>{message}</li>
								))}
							</ul>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
});

interface AlarmWizardProps {
	alarmId?: Dryp.AlarmId;
	onSuccess?: () => void;
}

const AlarmWizardImpl: React.FC<AlarmWizardProps> = ({ alarmId, onSuccess }) => {
	const {
		uiStore: { modalStore },
	} = useStore();
	const form = useForm<WizardFormData>({
		mode: "onChange",
		defaultValues: {
			criticalDeliveryMode: "immediate",
			warningThreshold: null,
			warningDeliveryMode: null,
			durationMinutes: null,
			datapointCount: null,
			structureIds: [],
			recipients: [],
			cooldownMinutes: 0,
		},
		resolver: zodResolver(wizardSchema),
	});
	const store = useMemo(() => new AlarmWizardStore(form, modalStore, alarmId, onSuccess), []);
	const isEditing = !!alarmId;

	return (
		<FormProvider {...form}>
			<AlarmWizardStoreProvider value={store}>
				<DialogHeader className="px-6 pt-6 pb-5 border-b shrink-0">
					<DialogTitle className="text-xl">{isEditing ? "Edit Alarm" : "Create Alarm"}</DialogTitle>
					<DialogDescription>
						{isEditing ? "Modify your alarm settings below." : "Set up a new alarm in a few simple steps."}
					</DialogDescription>
					<div className="pt-3">
						<WizardStepIndicator />
					</div>
				</DialogHeader>
				<AlarmWizardBody />
				<AlarmWizardFotter />
			</AlarmWizardStoreProvider>
		</FormProvider>
	);
};

export const AlarmWizard = Object.assign(AlarmWizardImpl, {
	dialogClassName: "sm:max-w-2xl p-0 gap-0 flex flex-col max-h-[90vh] overflow-hidden",
	disableOutsideClose: true,
});
